/**
 * Chat Store
 * Manages chat state and messages using Zustand
 */

import { create } from 'zustand';
import type { Subject } from '@kids-lms/types';
import { chatService, type SendMessageResponse, type ConversationListItem, type LessonStateResponse } from '../services';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: Date;
  moderationStatus?: string;
  isLoading?: boolean;
}

interface ChatState {
  // Current conversation
  currentConversationId: string | null;
  messages: ChatMessage[];
  isLoading: boolean;
  isSending: boolean;
  error: string | null;

  // Conversation list
  conversations: ConversationListItem[];
  hasMoreConversations: boolean;
  currentPage: number;

  // Current context
  currentChildId: string | null;
  currentSubject: Subject | null;
  currentCompanion: string | null;
  currentMode: string | null;
  lessonState: LessonStateResponse | null;

  // Actions
  setCurrentConversation: (conversationId: string | null) => void;
  setMessages: (messages: ChatMessage[]) => void;
  addMessage: (message: ChatMessage) => void;
  updateMessage: (messageId: string, updates: Partial<ChatMessage>) => void;
  setLoading: (loading: boolean) => void;
  setSending: (sending: boolean) => void;
  setError: (error: string | null) => void;
  setCurrentContext: (childId: string, subject?: Subject, companion?: string, mode?: string) => void;

  // Async actions
  sendMessage: (content: string) => Promise<SendMessageResponse | null>;
  loadConversation: (conversationId: string) => Promise<void>;
  loadConversations: (childId: string, page?: number) => Promise<void>;
  loadMoreConversations: () => Promise<void>;
  startNewConversation: (childId: string, subject?: Subject, companion?: string, mode?: string) => void;

  // Clear state
  clearChat: () => void;
  clearAll: () => void;
}

export const useChatStore = create<ChatState>()((set, get) => ({
  currentConversationId: null,
  messages: [],
  isLoading: false,
  isSending: false,
  error: null,
  conversations: [],
  hasMoreConversations: false,
  currentPage: 1,
  currentChildId: null,
  currentSubject: null,
  currentCompanion: null,
  currentMode: null,
  lessonState: null,

  setCurrentConversation: (conversationId) => set({ currentConversationId: conversationId }),

  setMessages: (messages) => set({ messages }),

  addMessage: (message) =>
    set((state) => ({
      messages: [...state.messages, message],
    })),

  updateMessage: (messageId, updates) =>
    set((state) => ({
      messages: state.messages.map((m) =>
        m.id === messageId ? { ...m, ...updates } : m
      ),
    })),

  setLoading: (isLoading) => set({ isLoading }),

  setSending: (isSending) => set({ isSending }),

  setError: (error) => set({ error }),

  setCurrentContext: (childId, subject, companion, mode) =>
    set({
      currentChildId: childId,
      currentSubject: subject || null,
      currentCompanion: companion || null,
      currentMode: mode || null,
    }),

  sendMessage: async (content) => {
    const { currentChildId, currentConversationId, currentSubject, currentCompanion, currentMode } = get();

    if (!currentChildId) {
      set({ error: 'No child selected' });
      return null;
    }

    // Pre-check content
    const preCheck = chatService.preCheckContent(content);
    if (!preCheck.valid) {
      set({ error: preCheck.message });
      return null;
    }

    // Check conversation turn limit
    const userMessageCount = get().messages.filter(m => m.role === 'user').length;
    if (userMessageCount >= 50) {
      set({ error: "We've been chatting a lot! Why don't you go do something fun and come back later? 🌟" });
      return null;
    }

    // Strip HTML tags for safety
    const sanitizedContent = content.replace(/<[^>]*>/g, '');

    // Add optimistic user message
    const tempUserMessageId = `temp-${Date.now()}`;
    const userMessage: ChatMessage = {
      id: tempUserMessageId,
      role: 'user',
      content: sanitizedContent,
      createdAt: new Date(),
    };

    // Add loading assistant message
    const tempAssistantMessageId = `temp-assistant-${Date.now()}`;
    const loadingMessage: ChatMessage = {
      id: tempAssistantMessageId,
      role: 'assistant',
      content: '',
      createdAt: new Date(),
      isLoading: true,
    };

    set((state) => ({
      messages: [...state.messages, userMessage, loadingMessage],
      isSending: true,
      error: null,
    }));

    try {
      const response = await chatService.sendMessage({
        childId: currentChildId,
        content: sanitizedContent,
        conversationId: currentConversationId || undefined,
        subject: currentSubject || undefined,
        companion: currentCompanion || undefined,
        mode: currentMode || undefined,
      });

      // Update conversation ID if new
      if (!currentConversationId) {
        set({ currentConversationId: response.conversationId });
      }

      // Replace temp messages with real ones
      set((state) => ({
        messages: state.messages
          .filter((m) => m.id !== tempUserMessageId && m.id !== tempAssistantMessageId)
          .concat([
            {
              id: response.message.id,
              role: 'user',
              content: response.message.content,
              createdAt: new Date(response.message.createdAt),
              moderationStatus: response.message.moderationStatus,
            },
            {
              id: response.aiResponse.id,
              role: 'assistant',
              content: response.aiResponse.content,
              createdAt: new Date(response.aiResponse.createdAt),
              moderationStatus: response.aiResponse.moderationStatus,
            },
          ]),
        isSending: false,
        lessonState: response.lessonState || state.lessonState,
      }));

      return response;
    } catch (error) {
      // Remove loading message and show error
      set((state) => ({
        messages: state.messages.filter((m) => m.id !== tempAssistantMessageId),
        isSending: false,
        error: error instanceof Error ? error.message : 'Failed to send message',
      }));
      return null;
    }
  },

  loadConversation: async (conversationId) => {
    set({ isLoading: true, error: null });

    try {
      const data = await chatService.getConversation(conversationId);

      set({
        currentConversationId: conversationId,
        messages: data.messages.map((m) => ({
          id: m.id,
          role: m.role,
          content: m.content,
          createdAt: new Date(m.createdAt),
          moderationStatus: m.moderationStatus,
        })),
        currentSubject: data.conversation.subject as Subject | null,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load conversation',
        isLoading: false,
      });
    }
  },

  loadConversations: async (childId, page = 1) => {
    set({ isLoading: true, error: null });

    try {
      const data = await chatService.getConversations(childId, page, 20);

      set({
        conversations: page === 1 ? data.conversations : [...get().conversations, ...data.conversations],
        hasMoreConversations: data.pagination.page < data.pagination.totalPages,
        currentPage: page,
        currentChildId: childId,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load conversations',
        isLoading: false,
      });
    }
  },

  loadMoreConversations: async () => {
    const { currentChildId, currentPage, hasMoreConversations, isLoading } = get();

    if (!currentChildId || !hasMoreConversations || isLoading) return;

    await get().loadConversations(currentChildId, currentPage + 1);
  },

  startNewConversation: (childId, subject, companion, mode) => {
    set({
      currentConversationId: null,
      messages: [],
      currentChildId: childId,
      currentSubject: subject || null,
      currentCompanion: companion || null,
      currentMode: mode || null,
      lessonState: null,
      error: null,
    });
  },

  clearChat: () =>
    set({
      currentConversationId: null,
      messages: [],
      error: null,
    }),

  clearAll: () =>
    set({
      currentConversationId: null,
      messages: [],
      isLoading: false,
      isSending: false,
      error: null,
      conversations: [],
      hasMoreConversations: false,
      currentPage: 1,
      currentChildId: null,
      currentSubject: null,
      currentCompanion: null,
      currentMode: null,
    }),
}));

export default useChatStore;
